import classNames from 'classnames';
import React from 'react';
import { Link } from 'react-router-dom';
import { ContentWrapper } from '../../components/content_wrapper/content_wrapper';
import { OmniaLinkButton } from '../../components/omnia_button/omnia_link_button';
import { ProPurchaseButton } from '../../components/pro_buttons/pro_purchase_button';
import { ProTrialButton } from '../../components/pro_buttons/pro_trial_button';
import { ScrollToTop } from '../../components/scroll_to_top/scroll_to_top';
import { SEO } from '../../components/seo/seo';
import SvgCheckMark from '../../components/svg/checkmark';
import SvgOmniaProLogo from '../../components/svg/omnia_pro_logo';
import { useResponsive } from '../../hooks/use_responsive';
import { useUser } from '../../hooks/use_user';
import { PRO_PURCHASE_URL, PRO_TRIAL_PURCHASE_URL } from '../../lib/stripe';
import { WebAnalytics } from '../../lib/web_analytics';
import heroGif from './assets/hero.gif';
import sethMobile from './assets/seth_hero_mobile.jpg';
import { ProComparisonChart } from './pro_comparison_chart';
import styles from './pro_page.less';

export const ProPage = () => {
  const { isUnderTablet, isDesktop } = useResponsive();
  const { isPro } = useUser();
  const pdpProPageUrl = '/p/omnia-fishing-omnia-pro-1-year-membership';

  return (
    <div>
      <ScrollToTop>
        <SEO
          title="Omnia PRO - Advanced Fishing Maps & Tools for Serious Anglers | Omnia Fishing"
          description="Join Omnia PRO to access advanced fishing maps, expert insights, and live habitat updates. Enjoy FREE shipping and 10% back on purchases. Sign up for $59/year."
        />
        <div className={styles.pageWrapper}>
          <section className={styles.headerWrapper}>
            <ContentWrapper noPadding className={styles.contentWrapper}>
              {isUnderTablet && (
                <img src={sethMobile} alt="picture of seth feider showing navionics depth map" />
              )}
              <SvgOmniaProLogo className={styles.proLogo__mobile} />
              <div className={styles.headerContainer}>
                <SvgOmniaProLogo className={styles.proLogo} />
                <div>
                  <span className="block text-orange mb-2 uppercase text-[13px] tracking-wider font-mono font-bold">
                    TRY FOR FREE
                  </span>
                  <h1>
                    Fish where the <br />
                    fish are
                  </h1>
                  <p className={styles.blurb}>
                    Omnia Pro is the Membership Where Detailed Fishing Maps and Shopping Rewards
                    Meet.
                  </p>
                  <div className="flex items-start gap-[6px] flex-wrap">
                    <ProTrialButton
                      className={styles.proButton}
                      size="lg"
                      kind="primary"
                      text="Try PRO 7-Days FREE"
                      trialPaymentLinkUrl={PRO_TRIAL_PURCHASE_URL}
                      onClick={() => {
                        WebAnalytics.proButtonClicked({
                          location: 'pro_page_hero',
                          type: 'trial',
                          style: 'button',
                        });
                      }}
                    />
                    {isPro ? (
                      <OmniaLinkButton size="lg" kind="primary" to={pdpProPageUrl}>
                        Buy PRO for a Friend
                      </OmniaLinkButton>
                    ) : (
                      <ProPurchaseButton
                        className={styles.proButton}
                        size="lg"
                        kind="tertiary"
                        text="Buy PRO for $59/year"
                        paymentLinkUrl={PRO_PURCHASE_URL}
                        onClick={() => {
                          WebAnalytics.proButtonClicked({
                            location: 'pro_page_hero',
                            type: 'purchase',
                            style: 'button',
                          });
                        }}
                      />
                    )}
                  </div>
                  {!isPro && (
                    <>
                      <div className="pt-4 flex sm:w-[330px] sm:justify-center mb-6">
                        <Link to={pdpProPageUrl} className="underline hover:text-white">
                          Buy PRO for a friend
                        </Link>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </ContentWrapper>
          </section>

          <section className="bg-[#E9EEEC] pt-12 pb-5 md:py-16">
            <ContentWrapper>
              <div className="mb-2 md:mb-4 flex flex-col sm:items-center">
                <span className={classNames(styles.orangeText, 'mb-1 sm:text-center')}>
                  Omnia Pro Features
                </span>
                <h2 className="sm:text-center font-mono font-thin mb-4 leading-tight sm:w-4/5">
                  {isDesktop
                    ? 'How Omnia PRO Makes Fishing More Rewarding'
                    : 'Elevate Your Fishing with Omnia PRO'}
                </h2>
              </div>
              <div className="flex flex-col sm:items-center">
                <ProComparisonChart />
                <div className="flex gap-[6px] items-center">
                  <ProTrialButton
                    size="lg"
                    kind="primary"
                    text="Start Your FREE 7-Day Trial Now"
                    trialPaymentLinkUrl={PRO_TRIAL_PURCHASE_URL}
                    className="my-6"
                    onClick={() => {
                      WebAnalytics.proButtonClicked({
                        location: 'pro_page_beneath_chart',
                        type: 'trial',
                        style: 'button',
                      });
                    }}
                  />

                  {isPro && (
                    <OmniaLinkButton size="lg" kind="primary" to={pdpProPageUrl}>
                      Buy PRO for a Friend
                    </OmniaLinkButton>
                  )}
                </div>
              </div>
            </ContentWrapper>
          </section>
          <section className="bg-[#061622]">
            <ContentWrapper className={styles.heroTwo}>
              <div className={styles.heroTwoBlurb}>
                <span className="block text-orange mt-8 mb-4 lg:mt-0 uppercase text-[13px] tracking-wider font-mono font-bold">
                  Omnia Pro Benefits
                </span>
                <h2 className="font-mono font-thin mb-4 leading-tight">
                  Access Layered Lake Maps for Desktop, Tablet, or Mobile
                </h2>
                <p>
                  Plan your fishing trip from any device, anywhere. All the lake info, local angler
                  reports and current conditions you need to eliminate dead water before you even
                  arrive.
                </p>
                <ul>
                  <li>
                    <SvgCheckMark color="#FF5229" fontSize={24} viewBox="0 0 24 24" />
                    <p>
                      <strong>Detailed Lake Info: </strong>Access Comprehensive Insights with
                      Navionics and C-MAP Charts — Including Depth Contours, Bottom Composition,
                      Water Clarity, Water Temperature, and Cross-Device Waypoint Sync.
                    </p>
                  </li>
                  <li>
                    <SvgCheckMark color="#FF5229" fontSize={24} viewBox="0 0 24 24" />
                    <p>
                      <strong>Safety Considerations: </strong>
                      Use Weather Radar, Storm Cells and Lightning Strike Data.
                    </p>
                  </li>
                  <li>
                    <SvgCheckMark color="#FF5229" fontSize={24} viewBox="0 0 24 24" />
                    <p>
                      <strong>Critical Weather Info: </strong> Wind, Cloud Cover and Barometric
                      Pressure.
                    </p>
                  </li>
                  <li>
                    <SvgCheckMark color="#FF5229" fontSize={24} viewBox="0 0 24 24" />
                    <p>
                      <strong>
                        Plus,<span> 10% back </span>
                        on gear purchases and free, fast shipping from our warehouse.
                      </strong>{' '}
                    </p>
                  </li>
                </ul>
              </div>
              <div className={styles.heroTwoGif}>
                <img src={heroGif} alt="" loading="lazy" className={styles.heroImg} />
              </div>
            </ContentWrapper>
          </section>
          {/* <section className="bg-[#E9EEEC] py-8 md:py-20">
            <ContentWrapper>
              <div className={styles.sectionThreeTitle}>
                <span className={classNames(styles.orangeText, 'mb-1 text-center')}>
                  Omnia PRO Map Layers
                </span>
                <h2 className="text-center font-mono font-thin mb-4 leading-tight">
                  8 Ways to Dial In Your Daily Pattern
                </h2>
              </div>
              <div className={styles.sectionThreeBody}>
                <div className={styles.sectionThreeRow__twoCards}>
                  <div className={styles.layerCard}>
                    <div className="flex flex-col items-center">
                      <span>Water Temperature</span>
                      <p>Spot Variations with Historic and Trending Water Temperature.</p>
                    </div>
                    <img
                      src={waterTemp}
                      alt="Image of a phone with Water Temp layer turned on"
                      loading="lazy"
                    />
                  </div>
                  <div className={styles.layerCard}>
                    <div className="flex flex-col items-center">
                      <span>Water Clarity</span>
                      <p>Tailor Your Tactics with Real-Time and Historical Insights.</p>
                    </div>
                    <img
                      src={waterClarity}
                      alt="Image of a phone with Water Clarity layer turned on"
                      loading="lazy"
                    />
                  </div>
                </div>
                <div className={styles.sectionThree__waypointRow}>
                  <div className={styles.sectionThree__waypointBlurb}>
                    <h3>Cross-Device Waypoint Sync</h3>
                    <p>
                      Sync Waypoints Across Mobile, Desktop and Tablet. Sort by Date and Filter by
                      Lake. Easy & Private Waypoint Sharing Between Friends or Family.
                    </p>
                  </div>
                  <div className={styles.waypointImageWrapper}>
                    <img src={waypointImage} alt="" loading="lazy" />
                  </div>
                </div>
                <div className={styles.sectionThreeRow__twoCards}>
                  <div className={styles.layerCard}>
                    <div className="flex flex-col items-center">
                      <span>Depth Contours</span>
                      <p>Compare Contours with Navionics and CMAP.</p>
                    </div>
                    <img
                      src={navionics}
                      alt="Image of a phone with Depth Contours layer turned on"
                      loading="lazy"
                    />
                  </div>
                  <div className={styles.layerCard}>
                    <div className="flex flex-col items-center">
                      <span>Bottom Composition</span>
                      <p>Understand your Waterbody's Hard and Soft Transition Zones.</p>
                    </div>
                    <img
                      src={waterClarity}
                      alt="Image of a phone with Bottom Composition layer turned on"
                      loading="lazy"
                    />
                  </div>
                </div>
                <div className={styles.sectionThreeRow__threeCards}>
                  <div className={styles.layerCard}>
                    <div className="flex flex-col items-center">
                      <span>Wind</span>
                      <p>Locate Wind-Pushed Bait.</p>
                    </div>
                    <img
                      src={wind}
                      alt="Image of a phone with Wind layer turned on"
                      loading="lazy"
                    />
                  </div>
                  <div className={styles.layerCard}>
                    <div className="flex flex-col items-center">
                      <span>Vegetation</span>
                      <p>Target Weed Lines.</p>
                    </div>
                    <img
                      src={vegetation}
                      alt="Image of a phone with vegetation layer turned on"
                      loading="lazy"
                    />
                  </div>
                  <div className={styles.layerCard}>
                    <div className="flex flex-col items-center">
                      <span>Weather</span>
                      <p>Live Storm, Temp, and Rain Updates.</p>
                    </div>
                    <img
                      src={weather}
                      alt="Image of a phone with a Weather layer turned on"
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
            </ContentWrapper>
          </section> */}
        </div>
      </ScrollToTop>
    </div>
  );
};
